import { onMounted, onUnmounted } from 'vue'

import type { ToastPayload } from '@app/shared/types/toast'
import useSfPolling from '@app/shared/vue/composables/use-sf-polling'
import { useToastStore } from '@app/shared/pinia/toast'
import { SfLog } from '@app/adapters/logger'

const MAX_PENDING_TOASTS = 20
const MAX_SHOWN_TOAST_IDS = 50

export function useMessageBusGlobalToasts(
  messageBusChannel: string,
  messageBusLastId: number
): void {
  const toastStore = useToastStore()
  const { startPolling } = useSfPolling<(payload: ToastPayload) => void>()

  const pendingToasts: ToastPayload[] = []
  const shownToastIds = new Set<string>()
  let stopPolling: (() => void) | null = null
  let broadcastChannel: BroadcastChannel | null = null

  function trackShownToastId(toastId: string): void {
    if (shownToastIds.size >= MAX_SHOWN_TOAST_IDS) {
      shownToastIds.delete(shownToastIds.values().next().value!)
    }
    shownToastIds.add(toastId)
  }

  function raiseToast(payload: ToastPayload): void {
    if (shownToastIds.has(payload.id)) return
    toastStore.raiseToast(payload)
    broadcastChannel?.postMessage({ toastId: payload.id })
  }

  function handleToastMessage(payload: ToastPayload): void {
    try {
      // Generic feature gate: dispatch a cancelable CustomEvent on
      // `document` before raising. Any feature-specific listener (e.g.
      // the insights segment-ready opt-in gate registered in setup) can
      // call `preventDefault()` to drop the toast — `dispatchEvent`
      // returns `false` when that happens. Keeps this composable free
      // of feature-specific imports.
      const beforeShow = new CustomEvent('toast:before-show', {
        detail: { payload },
        cancelable: true,
      })
      if (!document.dispatchEvent(beforeShow)) return

      if (document.visibilityState === 'visible') {
        raiseToast(payload)
      } else {
        if (pendingToasts.length >= MAX_PENDING_TOASTS) pendingToasts.shift()
        pendingToasts.push(payload)
      }
    } catch (error) {
      SfLog.error('Failed to raise global toast', {
        error,
        extra: { payload },
      })
    }
  }

  function handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      pendingToasts.splice(0).forEach(raiseToast)
    }
  }

  onMounted(() => {
    if (!messageBusChannel) {
      SfLog.warn('Message bus channel not found for toast subscription')
      return
    }

    try {
      broadcastChannel = new BroadcastChannel('sf-global-toasts')
      broadcastChannel.onmessage = (
        event: MessageEvent<{ toastId: string }>
      ): void => {
        trackShownToastId(event.data.toastId)
      }
    } catch {
      // BroadcastChannel unavailable, cross-tab deduplication disabled
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    stopPolling = startPolling(
      messageBusChannel,
      messageBusLastId,
      () => true,
      handleToastMessage
    )
  })

  onUnmounted(() => {
    stopPolling?.()
    stopPolling = null
    broadcastChannel?.close()
    broadcastChannel = null
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })
}
